// Redux slice for fetching, updating, deleting and creating layout specs

import { useRoleAuth } from '@src/hooks/useRoleAuth';
import type { PaginationParams } from '@src/providers/AppStateProvider/restEndpointBaseQuery';
import { ACTION, logger } from '@talos/kyoko';
import { useMemo } from 'react';
import type { LayoutSection, LayoutSpec } from '../../../types/LayoutConfig';
import { baseRestApi, createTag, sendToastOnQueryFulfilled } from '../../../utils/rtkQuery';

export interface GetLayoutSpecsParams extends PaginationParams {
  /** When true, uses the /ui/spaces-admin endpoint which gives access to all configs. Requires SpacesAdmin or Admin role. */
  useSpacesAdminApi?: boolean;
}

/** Base fields present on all share responses */
interface LayoutSpecShareBase {
  Timestamp: string;
  ConfigID: string;
  SharedBy: string;
  UpdateUser: string;
  Revision: number;
}

/** A share response from the API - can be user, role, or group based */
export type LayoutSpecShareResponse = LayoutSpecShareBase & LayoutSpecShareTarget;

/** A share target - user, role, or group */
export type LayoutSpecShareTarget = { User: string } | { Role: string } | { GroupID: string };

/** Layout spec response from the API */
export interface LayoutSpecResponse {
  Timestamp: string;
  ConfigID: string;
  User: string;
  UpdateUser: string;
  Revision: number;
  IsPublic: number;
  Shares: LayoutSpecShareResponse[];
  Type: string;
  Config: LayoutSpecTemplate;
}

/** Parameters for creating a new layout spec */
export interface CreateLayoutSpecParams {
  config: LayoutSpecTemplate;
  public?: boolean;
  shares?: LayoutSpecShareTarget[];
}

export type SpaceVisibility = 'template' | 'new-users' | 'all';

export interface LayoutSpecTemplate {
  id: string;
  label: string;
  layoutSpec: LayoutSpec;
  section: LayoutSection;
  availability?: SpaceVisibility;
}

/** Parameters for updating an existing layout spec */
export interface UpdateLayoutSpecParams {
  configID: string;
  config: LayoutSpecTemplate;
  public?: boolean;
  shares?: LayoutSpecShareTarget[];
  /** When true, uses the /ui/spaces-admin endpoint. Requires SpacesAdmin or Admin role. */
  useSpacesAdminApi?: boolean;
}

/** Parameters for deleting a layout spec */
export interface DeleteLayoutSpecParams {
  configID: string;
  label?: string; // Used for toast message
  /** When true, uses the /ui/spaces-admin endpoint. Requires SpacesAdmin or Admin role. */
  useSpacesAdminApi?: boolean;
}

const LAYOUT_SPEC_URL = '/ui/configuration/LayoutSpec';
const SPACES_ADMIN_URL = '/ui/spaces-admin';

function getLayoutSpecUrl(useSpacesAdminApi?: boolean, configID?: string) {
  const base = useSpacesAdminApi ? SPACES_ADMIN_URL : LAYOUT_SPEC_URL;
  return configID ? `${base}/${configID}` : base;
}

export function useLayoutSpecApiAccess() {
  const { isAuthorized } = useRoleAuth();

  return {
    canListAllSpaces: isAuthorized(ACTION.LIST_ALL_SPACES),
    canEditAllSpaces: isAuthorized(ACTION.EDIT_ALL_SPACES),
  };
}

const layoutSpecEndpoints = baseRestApi.injectEndpoints({
  endpoints: builder => ({
    /** Fetch all layout specs with optional pagination. Pass useSpacesAdminApi: true to fetch all users' templates. */
    getLayoutSpecs: builder.query<LayoutSpecResponse[], GetLayoutSpecsParams | void>({
      query: params => {
        const { useSpacesAdminApi, ...queryParams } = params ?? {};
        return {
          method: 'GET',
          url: getLayoutSpecUrl(useSpacesAdminApi),
          params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
        };
      },
      providesTags: result => {
        return result
          ? [...result.map(({ ConfigID }) => createTag('LayoutSpec', ConfigID)), createTag('LayoutSpec', 'LIST')]
          : [createTag('LayoutSpec', 'LIST')];
      },
      // Populate individual item caches from list response to avoid redundant fetches
      onQueryStarted: async (params, { dispatch, queryFulfilled }) => {
        try {
          const { data } = await queryFulfilled;
          const useSpacesAdminApi = (params as GetLayoutSpecsParams | undefined)?.useSpacesAdminApi;
          for (const layoutSpec of data) {
            dispatch(
              layoutSpecEndpoints.util.upsertQueryData(
                'getLayoutSpec',
                { configID: layoutSpec.ConfigID, useSpacesAdminApi },
                layoutSpec
              )
            );
          }
        } catch (error) {
          logger.error(error as Error);
        }
      },
    }),

    /** Fetch a single layout spec by ID */
    getLayoutSpec: builder.query<LayoutSpecResponse, { configID: string; useSpacesAdminApi?: boolean }>({
      query: ({ configID, useSpacesAdminApi }) => ({
        method: 'GET',
        url: getLayoutSpecUrl(useSpacesAdminApi, configID),
      }),
      transformResponse: (response: LayoutSpecResponse[]) => response[0],
      providesTags: (_result, _error, { configID }) => [createTag('LayoutSpec', configID)],
    }),

    /** Create a new layout spec */
    createLayoutSpec: builder.mutation<LayoutSpecResponse, CreateLayoutSpecParams>({
      query: ({ config, public: isPublic, shares }) => ({
        method: 'POST',
        url: LAYOUT_SPEC_URL,
        headers: { 'Content-Type': 'application/json' },
        body: {
          config,
          ...(isPublic != null && { public: isPublic }),
          ...(shares != null && { shares }),
        },
      }),
      transformResponse: (response: LayoutSpecResponse[]) => response[0],
      onQueryStarted: sendToastOnQueryFulfilled({
        successMessage: ({ config }) => `Template "${config.label}" created successfully.`,
        errorMessage: 'Could not create template',
      }),
      invalidatesTags: [createTag('LayoutSpec', 'LIST')],
    }),

    /** Update an existing layout spec */
    updateLayoutSpec: builder.mutation<LayoutSpecResponse, UpdateLayoutSpecParams>({
      query: ({ configID, config, public: isPublic, shares, useSpacesAdminApi }) => ({
        method: 'PUT',
        url: getLayoutSpecUrl(useSpacesAdminApi, configID),
        headers: { 'Content-Type': 'application/json' },
        body: {
          config,
          ...(isPublic != null && { public: isPublic }),
          ...(shares != null && { shares }),
        },
      }),
      transformResponse: (response: LayoutSpecResponse[]) => response[0],
      onQueryStarted: sendToastOnQueryFulfilled({
        successMessage: ({ config }) => `Template "${config.label}" updated successfully.`,
        errorMessage: 'Could not update template',
      }),
      invalidatesTags: (_result, _error, { configID }) => [
        createTag('LayoutSpec', configID),
        createTag('LayoutSpec', 'LIST'),
      ],
    }),

    /** Delete a layout spec */
    deleteLayoutSpec: builder.mutation<void, DeleteLayoutSpecParams>({
      query: ({ configID, useSpacesAdminApi }) => ({
        method: 'DELETE',
        url: getLayoutSpecUrl(useSpacesAdminApi, configID),
      }),
      onQueryStarted: sendToastOnQueryFulfilled({
        successMessage: ({ label }) => (label ? `Template "${label}" deleted.` : 'Template deleted.'),
        errorMessage: 'Could not delete template',
      }),
      invalidatesTags: (_result, _error, { configID }) => [
        createTag('LayoutSpec', configID),
        createTag('LayoutSpec', 'LIST'),
      ],
    }),
  }),
});

const {
  useGetLayoutSpecsQuery: useGetLayoutSpecsBaseQuery,
  useGetLayoutSpecQuery,
  useCreateLayoutSpecMutation,
  useUpdateLayoutSpecMutation,
  useDeleteLayoutSpecMutation,
} = layoutSpecEndpoints;

export function useLayoutSpecsQuery(
  params?: Omit<GetLayoutSpecsParams, 'useSpacesAdminApi'>,
  options?: { skip?: boolean }
) {
  const { canListAllSpaces } = useLayoutSpecApiAccess();
  const queryArgs = useMemo<GetLayoutSpecsParams | undefined>(
    () => (canListAllSpaces ? { ...params, useSpacesAdminApi: true } : params),
    [canListAllSpaces, params]
  );

  return useGetLayoutSpecsBaseQuery(queryArgs, options);
}

export { useCreateLayoutSpecMutation, useDeleteLayoutSpecMutation, useGetLayoutSpecQuery, useUpdateLayoutSpecMutation };
